import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Alert,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleIcon from '@mui/icons-material/Article';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { useAgreementDetailController } from './context/agreement-detail-controller';
import { useAgreementDetailState } from './context/agreement-detail-context';

export function AgreementDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadDetail } = useAgreementDetailController();
  const { agreement, loading, error } = useAgreementDetailState();

  useEffect(() => {
    if (id) {
      loadDetail(parseInt(id));
    }
  }, [id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={3} />;
  }

  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  if (!agreement) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mt: 2 }}>
            Convênio/repasse não encontrado.
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={`Convênio/Repasse ${agreement.agreementNumber || agreement.id}`}
          subtitle={agreement.superiorBody || 'Detalhes do convênio/repasse'}
          breadcrumbs={[
            { label: 'Convênios e Repasses', href: '/convenios-repasses' },
            { label: agreement.agreementNumber || `#${agreement.id}` },
          ]}
        />

        {/* Informações Principais */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ArticleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {agreement.agreementNumber || `Convênio #${agreement.id}`}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            {agreement.description && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                  <DescriptionIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Descrição
                    </Typography>
                    <Typography variant="body1">{agreement.description}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Órgão Superior
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {agreement.superiorBody || 'Não informado'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Órgão Subordinado
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {agreement.subordinateBody || 'Não informado'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Unidade Gestora
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {agreement.managementUnit || 'Não informado'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Período de Vigência
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatDate(agreement.startDate)} até {formatDate(agreement.endDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Valores Financeiros */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachMoneyIcon sx={{ mr: 1 }} />
            Valores Financeiros
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Valor de Repasse
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="primary.main">
                    {formatCurrency(agreement.transferAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Valor de Contrapartida
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="success.main">
                    {formatCurrency(agreement.counterpartAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Botão de voltar - centralizado no final */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/convenios-repasses')}
            size="large"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
            }}
          >
            Voltar para Convênios e Repasses
          </Button>
        </Box>
      </Container>
    </PageContainer>
  );
}
